import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Loader2, Search } from 'lucide-react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import ShopSelect from '../../../components/ShopSelect';
import FulfillmentQueueTabs from '../../components/orders/FulfillmentQueueTabs';
import OrderOperationsTable from '../../components/orders/OrderOperationsTable';
import OrdersWorkspaceHeader from '../../components/orders/OrdersWorkspaceHeader';
import { businessFulfillmentService } from '../../services/businessFulfillmentService';
import {
  normalizeQueueGroup,
  queueGroupDefinition,
  queueStageParam,
} from '../../utils/ordersOperationsPresentation';

function refreshedAt(value) {
  if (!value) return 'Waiting for current queue data';
  return `Last refreshed ${value.toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
}

export default function FulfillmentWorkbench() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const group = normalizeQueueGroup(params.get('group') || params.get('stage'));
  const groupDefinition = queueGroupDefinition(group);
  const requestedPage = Number(params.get('page') || 1);
  const page = Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1;
  const requestedSort = params.get('sort') || 'oldest';
  const sort = ['oldest', 'newest'].includes(requestedSort) ? requestedSort : 'oldest';
  const orderId = params.get('orderId');
  const query = params.get('q') || '';
  const [search, setSearch] = useState(query);
  const [data, setData] = useState({ items: [], counts: {}, meta: { page: 1, totalPages: 1, total: 0 }, capabilities: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const requestRef = useRef(0);

  const updateParams = useCallback(changes => setParams(current => {
    const next = new URLSearchParams(current);
    Object.entries(changes).forEach(([key, value]) => {
      if (value == null || value === '') next.delete(key);
      else next.set(key, String(value));
    });
    return next;
  }), [setParams]);

  useEffect(() => { setSearch(query); }, [query]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search !== query) updateParams({ q: search, page: 1, orderId: null });
    }, 300);
    return () => clearTimeout(timer);
  }, [search, query, updateParams]);

  const load = useCallback(async ({ quiet = false } = {}) => {
    const requestId = ++requestRef.current;
    if (!quiet) setLoading(true);
    setError('');
    try {
      const next = await businessFulfillmentService.list({
        stage: queueStageParam(group), q: query, page, limit: 25, sort,
      });
      if (requestId === requestRef.current) {
        setData(next);
        setUpdatedAt(new Date());
      }
    } catch {
      if (requestId === requestRef.current) setError('Could not load fulfillment work. Check your connection and try again.');
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, [group, query, page, sort]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load({ quiet: true });
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const heading = useMemo(() => (
    data.meta?.total === 1 ? '1 open task' : `${data.meta?.total || 0} open tasks`
  ), [data.meta]);
  const unhealthy = ['DEGRADED', 'UNAVAILABLE', 'UNKNOWN'].includes(data.workflowHealth?.state);

  if (orderId) {
    const context = new URLSearchParams(params);
    context.delete('orderId');
    const suffix = context.toString();
    return <Navigate to={`/business/orders/fulfillment/${orderId}${suffix ? `?${suffix}` : ''}`} replace />;
  }

  return (
    <main className="min-w-0 space-y-5 text-neutral-950 dark:text-white">
      <OrdersWorkspaceHeader
        eyebrow="Logistics / Fulfillment"
        title="Fulfillment queue"
        subtitle="Orders grouped by what needs doing next: pack them, hand them to a rider, or sort out a problem."
        meta={refreshedAt(updatedAt)}
      >
        <Link to="/business/orders" className="business-primary-action inline-flex min-h-11 items-center rounded-lg border border-neutral-300 px-4 text-sm font-semibold hover:bg-neutral-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:hover:bg-neutral-900">Order records</Link>
      </OrdersWorkspaceHeader>

      {unhealthy ? (
        <div role="status" className="rounded-xl border border-warning bg-warning-subtle p-4 text-sm text-warning">
          <p className="font-semibold">Automatic handoff processing needs attention</p>
          <p className="mt-1">Ready parcels remain safely queued. Check Logistics for the current status; normal fulfillment does not require a manual dispatch action.</p>
        </div>
      ) : null}

      <section className="min-w-0 overflow-hidden rounded-panel border border-default bg-surface shadow-panel">
        <div className="space-y-4 border-b border-neutral-200 p-4 dark:border-neutral-800">
          <FulfillmentQueueTabs
            activeGroup={group}
            counts={data.counts}
            onChange={nextGroup => updateParams({ group: nextGroup, stage: null, page: 1, orderId: null })}
          />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <label className="relative block min-w-0 sm:max-w-md sm:flex-1">
              <span className="sr-only">Search fulfillment orders</span>
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-neutral-400" aria-hidden="true" />
              <input name="fulfillmentSearch" autoComplete="off" value={search} onChange={event => setSearch(event.target.value)} placeholder="Search order or customer…" className="min-h-11 w-full rounded-lg border border-neutral-200 bg-white pl-10 pr-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-focus dark:border-neutral-700 dark:bg-neutral-900" />
            </label>
            <div className="flex items-center justify-between gap-2 text-xs text-neutral-500 sm:justify-start">
              <span>Sort</span>
              <ShopSelect
                value={sort}
                onValueChange={value => updateParams({ sort: value, page: 1, orderId: null })}
                options={[
                  { value: 'oldest', label: 'Oldest first' },
                  { value: 'newest', label: 'Newest first' },
                ]}
                ariaLabel="Sort fulfillment orders"
                align="end"
                minPanelWidth={144}
                triggerClassName="min-h-11 w-36 rounded-lg px-3 text-sm"
                panelClassName="py-1"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
          <div>
            <p className="font-medium">{heading}</p>
            <p className="mt-0.5 text-xs text-neutral-500">{groupDefinition.label}</p>
          </div>
          <p className="hidden text-xs text-neutral-500 sm:block">Waiting age · {sort === 'oldest' ? 'oldest first' : 'newest first'}</p>
        </div>

        {error ? (
          <div role="alert" className="m-4 rounded-xl border border-danger bg-danger-subtle p-5 text-sm text-danger">
            <AlertCircle className="mb-2 h-5 w-5" aria-hidden="true" />
            {error}
            <button type="button" onClick={() => load()} className="ml-2 min-h-10 font-semibold underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus">Try again</button>
          </div>
        ) : loading ? (
          <div role="status" className="grid min-h-64 place-items-center">
            <Loader2 className="h-7 w-7 animate-spin text-accent motion-reduce:animate-none" aria-hidden="true" />
            <span className="sr-only">Loading fulfillment queue…</span>
          </div>
        ) : (
          <OrderOperationsTable
            items={data.items || []}
            onOpen={item => navigate(`/business/orders/fulfillment/${item.orderId}?${params.toString()}`)}
            emptyTitle={groupDefinition.emptyTitle}
            emptyDescription={groupDefinition.emptyDescription}
          />
        )}

        <footer className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 dark:border-neutral-800">
          <p className="text-xs text-neutral-500">Page {data.meta?.page || 1} of {data.meta?.totalPages || 1}</p>
          <div className="flex gap-2">
            <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => updateParams({ page: page - 1, orderId: null })} className="grid h-10 w-10 place-items-center rounded-lg border border-neutral-200 disabled:opacity-40 dark:border-neutral-700"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" aria-label="Next page" disabled={page >= (data.meta?.totalPages || 1)} onClick={() => updateParams({ page: page + 1, orderId: null })} className="grid h-10 w-10 place-items-center rounded-lg border border-neutral-200 disabled:opacity-40 dark:border-neutral-700"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </footer>
      </section>

    </main>
  );
}
